import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import {
  Lightbulb,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Shield,
  X,
  ChevronRight,
  Sparkles,
  CheckCircle2,
  Target,
  PieChart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const INSIGHT_ICON_MAP = {
  AlertTriangle, TrendingUp, Shield, CheckCircle2, PieChart, Target, Sparkles
};

export default function SmartInsightsPage() {
  const { user } = useAuth();
  const { insightTypeMap, priorityLevelMap } = useAppData();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [insights, setInsights] = useState([]);
  const [dismissedInsights, setDismissedInsights] = useState([]);
  const [showDismissed, setShowDismissed] = useState(false);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const [activeRes, dismissedRes] = await Promise.all([
        axios.get(`${API}/ai/insights?dismissed=false`),
        axios.get(`${API}/ai/insights?dismissed=true`)
      ]);
      setInsights(activeRes.data);
      setDismissedInsights(dismissedRes.data);
    } catch (err) {
      console.error('Error fetching insights:', err);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const generateInsights = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/insights/generate`);
      setInsights(prev => [...response.data, ...prev]);
      toast.success(`Generated ${response.data.length} new insights`);
    } catch (err) {
      console.error('Error generating insights:', err);
      toast.error('Failed to generate insights');
    } finally {
      setGenerating(false);
    }
  };

  const dismissInsight = async (insightId) => {
    try {
      await axios.post(`${API}/ai/insights/${insightId}/dismiss`);
      const dismissed = insights.find(i => i.id === insightId);
      setInsights(prev => prev.filter(i => i.id !== insightId));
      if (dismissed) {
        setDismissedInsights(prev => [dismissed, ...prev]);
      }
      toast.success('Insight dismissed');
    } catch (err) {
      toast.error('Failed to dismiss insight');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#ffd87a] animate-spin mx-auto mb-4" />
          <p className="text-[#4a4a4a]">Loading insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="smart-insights-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
            <Lightbulb className="w-5 h-5 text-[#1a1a1a]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1a1a1a]">Smart Insights</h1>
            <p className="text-sm text-[#4a4a4a]">AI-powered portfolio recommendations</p>
          </div>
        </div>
        <Button
          onClick={generateInsights}
          disabled={generating}
          className="bg-[#ffd879] hover:bg-[#f5cc6a] text-black"
        >
          {generating ? (
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2" />
          )}
          Generate Insights
        </Button>
      </div>

      <div className="space-y-3">
        {/* Active Insights */}
        {insights.length > 0 ? (
          <div className="space-y-4">
            {insights.map((insight) => {
              const Icon = INSIGHT_ICON_MAP[insightTypeMap[insight.insight_type]?.data?.icon_name] || Lightbulb;
              const priorityClass = priorityLevelMap[insight.priority]?.data?.color || 'bg-amber-500/20 text-amber-400 border-amber-500/30';

              return (
                <Card key={insight.id} className="bg-white border-[#e5e5e5] overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex">
                      {/* Priority indicator */}
                      <div className={`w-1.5 ${
                        insight.priority === 'high' ? 'bg-red-500' :
                        insight.priority === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                      }`} />
                      
                      <div className="flex-1 p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              insight.priority === 'high' ? 'bg-red-500/20' :
                              insight.priority === 'medium' ? 'bg-amber-500/20' : 'bg-emerald-500/20'
                            }`}>
                              <Icon className={`w-5 h-5 ${
                                insight.priority === 'high' ? 'text-red-400' :
                                insight.priority === 'medium' ? 'text-amber-400' : 'text-emerald-400'
                              }`} />
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-[#1a1a1a]">{insight.title}</h3>
                                <Badge className={priorityClass}>
                                  {insight.priority.toUpperCase()}
                                </Badge>
                              </div>
                              <p className="text-[#4a4a4a] text-sm mb-3">{insight.description}</p>
                              
                              {insight.action_items && insight.action_items.length > 0 && (
                                <div className="space-y-1">
                                  <p className="text-xs text-[#4a4a4a] uppercase tracking-wider">Recommended Actions:</p>
                                  <ul className="space-y-1">
                                    {insight.action_items.map((action, i) => (
                                      <li key={i} className="flex items-center gap-2 text-sm text-[#4a4a4a]">
                                        <ChevronRight className="w-3 h-3 text-[#4a4a4a]" />
                                        {action}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              <p className="text-xs text-[#4a4a4a] mt-3">
                                Generated {formatDate(insight.created_at)}
                              </p>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => dismissInsight(insight.id)}
                            className="p-2 rounded-lg hover:bg-[#f5f5f5] text-[#4a4a4a] hover:text-[#4a4a4a] transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="bg-white border-[#e5e5e5]">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-[#f5f5f5] flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="w-8 h-8 text-[#4a4a4a]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a1a1a] mb-2">No Active Insights</h3>
              <p className="text-[#4a4a4a] mb-6">Generate new insights to get AI-powered recommendations for your portfolio</p>
              <Button
                onClick={generateInsights}
                disabled={generating}
                className="bg-[#ffd879] hover:bg-[#f5cc6a] text-black"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Generate Insights
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Dismissed Insights */}
        {dismissedInsights.length > 0 && (
          <div className="mt-8">
            <button
              onClick={() => setShowDismissed(!showDismissed)}
              className="flex items-center gap-2 text-[#4a4a4a] hover:text-[#2a2a2a] text-sm mb-4"
            >
              <ChevronRight className={`w-4 h-4 transition-transform ${showDismissed ? 'rotate-90' : ''}`} />
              {dismissedInsights.length} dismissed insights
            </button>
            
            {showDismissed && (
              <div className="space-y-2 opacity-60">
                {dismissedInsights.map((insight) => (
                  <Card key={insight.id} className="bg-[#f9f9f9] border-[#e5e5e5]">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-4 h-4 text-[#4a4a4a]" />
                        <div className="flex-1">
                          <p className="text-sm text-[#4a4a4a]">{insight.title}</p>
                          <p className="text-xs text-[#4a4a4a]">{formatDate(insight.created_at)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
